/**
 * Restrições do usuário logado na impressão avulsa, configuradas no backoffice
 * (Configurações > Usuários). Nada configurado = nada restrito, que é o estado
 * de todo mundo hoje.
 *
 * - Localização: a MESMA restrição que o backoffice e o app Rotas aplicam
 *   (`niveis_acesso`, allow-list `niveisN` e deny-list `niveisN_bloqueados`),
 *   valendo só com `perfil_personalizavel[0].permissoes[0].itens.visualizar`
 *   ligado — é a chave "Aplicar esta restrição" da tela do usuário.
 * - Categoria: `perfil_personalizavel[].permissoes[].impressao_categorias`,
 *   allow-list de ids. Vazia = todas.
 * - Perfil administrador não fica restrito.
 * - O grupo de usuários do usuário soma do mesmo jeito que no resto do produto
 *   (ver permissoesGrupoUsuario.ts).
 */

import { aplicarGrupoUsuario, ehAdmin, type UsuarioComGrupo } from "@/lib/permissoesGrupoUsuario";

type EntradaNivel = Record<string, string | undefined>;

type NivelAcesso = Partial<Record<string, EntradaNivel[]>>;

type Permissoes = {
  itens?: { visualizar?: boolean };
  impressao_categorias?: string[];
};

export type UsuarioComRestricoes = UsuarioComGrupo & {
  perfil?: string;
  niveis_acesso?: NivelAcesso[];
  perfil_personalizavel?: { permissoes?: Permissoes[] }[];
};

export type RestricoesImpressao = {
  /** Por nível (1..4): ids permitidos. Vazio = sem allow-list no nível. */
  permitidos: Record<number, string[]>;
  /** Por nível (1..4): ids bloqueados. */
  bloqueados: Record<number, string[]>;
  /** Ids de categoria liberados. Vazio = todas. */
  categorias: string[];
};

export const SEM_RESTRICAO: RestricoesImpressao = {
  permitidos: {},
  bloqueados: {},
  categorias: [],
};

const NIVEIS = [1, 2, 3, 4];

function ids(lista: EntradaNivel[] | undefined, nivel: number): string[] {
  return (lista || [])
    .map((e) => e?.[`id_nivel_loc${nivel}`])
    .filter((id): id is string => !!id && id.trim() !== "" && id !== "*");
}

export function restricoesDoUsuario(
  dados: UsuarioComRestricoes | null | undefined,
): RestricoesImpressao {
  if (!dados) return SEM_RESTRICAO;
  if (ehAdmin(dados.perfil)) return SEM_RESTRICAO;
  const usuario = aplicarGrupoUsuario(dados) || dados;

  const permissoes = (usuario.perfil_personalizavel || []).flatMap(
    (p) => p.permissoes || [],
  );
  const categorias = [
    ...new Set(permissoes.flatMap((p) => p.impressao_categorias || [])),
  ].filter(Boolean);

  const visualizar =
    usuario.perfil_personalizavel?.[0]?.permissoes?.[0]?.itens?.visualizar === true;
  const acesso = usuario.niveis_acesso?.[0];

  const permitidos: Record<number, string[]> = {};
  const bloqueados: Record<number, string[]> = {};
  if (visualizar && acesso) {
    for (const n of NIVEIS) {
      permitidos[n] = ids(acesso[`niveis${n}`], n);
      bloqueados[n] = ids(acesso[`niveis${n}_bloqueados`], n);
    }
  }

  return { permitidos, bloqueados, categorias };
}

/** O id daquele nível pode ser usado? */
export function localizacaoPermitida(
  r: RestricoesImpressao,
  nivel: number,
  id: string | undefined,
): boolean {
  if (!id) return true;
  if ((r.bloqueados[nivel] || []).includes(id)) return false;
  const allow = r.permitidos[nivel] || [];
  return !(allow.length && !allow.includes(id));
}

type No = { _id: string; children?: No[] };

/**
 * Poda a árvore de localizações: some o nó restrito e tudo abaixo dele. Basta
 * um nível restrito para esconder o caminho — se a filial está fora, não
 * importa que o setor esteja liberado.
 */
export function filtrarArvoreLocalizacoes<T extends No>(
  arvore: T[],
  r: RestricoesImpressao,
  nivel = 1,
): T[] {
  const temRestricao = NIVEIS.some(
    (n) => (r.permitidos[n] || []).length || (r.bloqueados[n] || []).length,
  );
  if (!temRestricao) return arvore;
  return arvore
    .filter((no) => localizacaoPermitida(r, nivel, no._id))
    .map((no) =>
      no.children
        ? { ...no, children: filtrarArvoreLocalizacoes(no.children, r, nivel + 1) }
        : no,
    );
}

/** Categorias que o usuário pode usar (lista inteira quando não há restrição). */
export function filtrarCategorias<T extends { _id: string }>(
  categorias: T[],
  r: RestricoesImpressao,
): T[] {
  if (!r.categorias.length) return categorias;
  const liberadas = new Set(r.categorias);
  return categorias.filter((c) => liberadas.has(c._id));
}
